/**
 * Procedural space MMO audio — Web Audio API only (zero external assets).
 * Retro-futuristic / Battlezone-adjacent tone: cold oscillators, filtered noise, vector-era UI bleeps.
 */

/**
 * Maps both weapon ids and `visualStyle` tokens to one of the six
 * procedural presets defined below. Missing keys fall back to the pulse
 * blaster preset via the `default` branch in `playWeaponSound`.
 */
const WEAPON_ALIASES: Record<string, string> = {
  // pulse family
  pulse: 'pulse_blaster',
  pulse_blaster: 'pulse_blaster',
  blaster: 'pulse_blaster',
  energy: 'pulse_blaster',

  // laser / beam family
  mining_laser: 'mining_laser',
  laser: 'mining_laser',
  beam: 'mining_laser',
  ion_beam: 'mining_laser',
  arc_welder: 'mining_laser',
  arc: 'mining_laser',
  void_beam: 'mining_laser',
  void: 'mining_laser',
  ether_harp: 'mining_laser',
  ether: 'mining_laser',

  // rapid-fire kinetic family
  gatling: 'gatling_cannon',
  gatling_cannon: 'gatling_cannon',
  needler: 'gatling_cannon',
  needle_repeater: 'gatling_cannon',
  shotgun: 'gatling_cannon',
  scatter_shotgun: 'gatling_cannon',
  disruptor: 'gatling_cannon',
  disruptor_array: 'gatling_cannon',

  // plasma / heavy energy family
  plasma: 'plasma_cannon',
  plasma_cannon: 'plasma_cannon',
  antimatter: 'plasma_cannon',
  antimatter_howitzer: 'plasma_cannon',
  nova: 'plasma_cannon',
  nova_lance: 'plasma_cannon',
  singularity: 'plasma_cannon',
  singularity_cannon: 'plasma_cannon',
  abyssal_maw: 'plasma_cannon',

  // railgun / coil family
  rail: 'railgun',
  railgun: 'railgun',
  coil: 'railgun',
  coil_driver: 'railgun',
  prism: 'railgun',
  prism_splitter: 'railgun',
  chrono: 'railgun',
  chrono_lance: 'railgun',

  // missile / torpedo family
  missile: 'missile_launch',
  missile_launch: 'missile_launch',
  light_missiles: 'missile_launch',
  swarm: 'missile_launch',
  swarm_missiles: 'missile_launch',
  torpedo: 'missile_launch',
  emp_torpedoes: 'missile_launch',
  cluster: 'missile_launch',
  cluster_rockets: 'missile_launch',
};

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function now(ctx: BaseAudioContext): number {
  return ctx.currentTime;
}

export class AudioManager {
  private ctx: AudioContext | null = null;

  private masterGain: GainNode | null = null;

  private masterVolume = 1;

  private noiseBuffer: AudioBuffer | null = null;

  /** Ambient drone subgraph */
  private ambientOscs: OscillatorNode[] = [];

  private ambientLfo: OscillatorNode | null = null;

  private ambientLfoGain: GainNode | null = null;

  private ambientMaster: GainNode | null = null;

  private ambientRunning = false;

  /**
   * Engine "thruster" graph — looping white noise through a bandpass whose
   * centre frequency tracks throttle, layered with a low rumble. Gives the
   * air-propulsor / rocket exhaust feel (no tonal sawtooth hum).
   */
  private engineNoise: AudioBufferSourceNode | null = null;

  private engineNoiseGain: GainNode | null = null;

  private engineBandpass: BiquadFilterNode | null = null;

  private engineHighpass: BiquadFilterNode | null = null;

  private engineRumble: OscillatorNode | null = null;

  private engineRumbleGain: GainNode | null = null;

  private engineSpeed = 0;

  /**
   * Background music channel.
   *
   * We use an `HTMLAudioElement` routed through `MediaElementAudioSourceNode`
   * rather than `decodeAudioData` + `AudioBufferSourceNode` so long tracks
   * stream from the network without buffering the whole file, and the
   * browser's built-in `loop` attribute handles seamless looping.
   *
   * The dedicated `musicGain` node lets us set a music-only level (10% by
   * default) on top of the master volume, so bumping `setMasterVolume(1)`
   * doesn't also blow out the music bed.
   */
  private musicElement: HTMLAudioElement | null = null;

  private musicSource: MediaElementAudioSourceNode | null = null;

  private musicGain: GainNode | null = null;

  /** Pending music URL + volume if `startMusic` was called before `init`. */
  private pendingMusic: { url: string; volume: number } | null = null;

  /**
   * User preference — when `false`, the music element is kept around but
   * paused so toggling back on resumes instantly without re-downloading
   * the track.
   */
  private musicEnabled = true;

  private disposed = false;

  /**
   * Must be called from a user gesture (click/tap/key) so the AudioContext can start.
   */
  init(): void {
    if (this.disposed || this.ctx) return;

    const ctx = new AudioContext();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = this.masterVolume;
    master.connect(ctx.destination);
    this.masterGain = master;

    const seconds = 3;
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;

    // Long-loop noise buffer dedicated to the engine thruster so its loop
    // boundary is inaudible at any throttle.
    const engineBuf = this.buildEngineNoiseBuffer(ctx);
    this.buildEngineGraph(ctx, master, engineBuf);
    void ctx.resume();

    // If a caller queued music before `init` (common when startGame fires
    // multiple audio calls in one user-gesture tick), spin it up now.
    if (this.pendingMusic) {
      const { url, volume } = this.pendingMusic;
      this.pendingMusic = null;
      this.startMusic(url, volume);
    }
  }

  private buildEngineNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const seconds = 6;
    const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    // Pink-ish noise approximation (Voss-style) for a rounder air sound.
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < ch.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      ch[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.18;
      b6 = white * 0.115926;
    }
    return buf;
  }

  private ensureCtx(): AudioContext | null {
    if (this.disposed || !this.ctx) return null;
    return this.ctx;
  }

  private buildEngineGraph(ctx: AudioContext, master: GainNode, noiseBuf: AudioBuffer): void {
    // Core air-rush: looping pink noise shaped by a high-Q bandpass whose
    // centre frequency tracks throttle and a gentle highpass so idle is
    // soft and full thrust is brighter/louder (airy "whoosh").
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;

    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 180;
    hp.Q.value = 0.5;

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 300;
    bp.Q.value = 0.7;

    const g = ctx.createGain();
    g.gain.value = 0;

    // Sub rumble adds body — inaudible at idle, felt under full thrust.
    const rumble = ctx.createOscillator();
    rumble.type = 'sine';
    rumble.frequency.value = 48;
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.value = 0;

    noise.connect(hp);
    hp.connect(bp);
    bp.connect(g);
    g.connect(master);

    rumble.connect(rumbleGain);
    rumbleGain.connect(master);

    noise.start();
    rumble.start();

    this.engineNoise = noise;
    this.engineNoiseGain = g;
    this.engineBandpass = bp;
    this.engineHighpass = hp;
    this.engineRumble = rumble;
    this.engineRumbleGain = rumbleGain;
  }

  /**
   * Map weapon id (and optional visual-style fallback) to internal
   * procedural presets. `visualStyle` is the broader family token carried
   * on projectile spawns — using it as a fallback means newly added
   * weapons get a sensible sound for free.
   */
  playWeaponSound(weaponId: string, visualStyle?: string): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;

    const lowered = weaponId.toLowerCase();
    const styleKey = visualStyle?.toLowerCase();
    const key =
      WEAPON_ALIASES[lowered] ??
      (styleKey ? WEAPON_ALIASES[styleKey] : undefined) ??
      lowered;
    const t = now(ctx);

    switch (key) {
      case 'pulse_blaster':
        this.playPulseBlaster(ctx, this.masterGain, t);
        break;
      case 'mining_laser':
        this.playMiningLaser(ctx, this.masterGain, t);
        break;
      case 'gatling_cannon':
        this.playGatlingCannon(ctx, this.masterGain, t);
        break;
      case 'plasma_cannon':
        this.playPlasmaCannon(ctx, this.masterGain, t);
        break;
      case 'railgun':
        this.playRailgun(ctx, this.masterGain, t);
        break;
      case 'missile_launch':
        this.playMissileLaunch(ctx, this.masterGain, t);
        break;
      default:
        this.playPulseBlaster(ctx, this.masterGain, t);
    }
  }

  /** Short sine burst + quick downward pitch sweep */
  private playPulseBlaster(ctx: AudioContext, out: AudioNode, t: number): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(220, t + 0.06);

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.22, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.09);

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1200;
    bp.Q.value = 0.7;

    osc.connect(bp);
    bp.connect(g);
    g.connect(out);

    osc.start(t);
    osc.stop(t + 0.1);
  }

  /**
   * Crisp sci-fi beam zap. Layers a rising triangle tone, a warm sine body
   * and a brief bandpassed-noise shimmer, with a short single-tap delay for
   * "laser ricochet" character. Replaces the old muddy sawtooth which felt
   * cheaper than the pulse blaster.
   */
  private playMiningLaser(ctx: AudioContext, out: AudioNode, t: number): void {
    const dur = 0.22;

    // ── Pre-delay mix bus so both tones and shimmer share the same tail.
    const mix = ctx.createGain();
    mix.gain.value = 1;

    // Main high zap — rising pitch reads as "charged energy release".
    const lead = ctx.createOscillator();
    lead.type = 'triangle';
    lead.frequency.setValueAtTime(1600, t);
    lead.frequency.exponentialRampToValueAtTime(2400, t + 0.05);
    lead.frequency.exponentialRampToValueAtTime(900, t + dur);

    const leadG = ctx.createGain();
    leadG.gain.setValueAtTime(0.001, t);
    leadG.gain.exponentialRampToValueAtTime(0.18, t + 0.006);
    leadG.gain.exponentialRampToValueAtTime(0.001, t + dur);

    lead.connect(leadG);
    leadG.connect(mix);

    // Warm sub-body sine gives the beam weight (prevents thinness).
    const body = ctx.createOscillator();
    body.type = 'sine';
    body.frequency.setValueAtTime(440, t);
    body.frequency.exponentialRampToValueAtTime(320, t + dur);

    const bodyG = ctx.createGain();
    bodyG.gain.setValueAtTime(0.001, t);
    bodyG.gain.exponentialRampToValueAtTime(0.09, t + 0.012);
    bodyG.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.9);

    body.connect(bodyG);
    bodyG.connect(mix);

    // Metallic shimmer — narrow bandpassed noise for "sizzle".
    if (this.noiseBuffer) {
      const nSrc = ctx.createBufferSource();
      nSrc.buffer = this.noiseBuffer;
      const nf = ctx.createBiquadFilter();
      nf.type = 'bandpass';
      nf.frequency.setValueAtTime(4500, t);
      nf.frequency.exponentialRampToValueAtTime(2800, t + dur);
      nf.Q.value = 4;

      const nG = ctx.createGain();
      nG.gain.setValueAtTime(0.001, t);
      nG.gain.exponentialRampToValueAtTime(0.08, t + 0.008);
      nG.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      nSrc.connect(nf);
      nf.connect(nG);
      nG.connect(mix);
      nSrc.start(t);
      nSrc.stop(t + dur + 0.02);
    }

    // Short feedback delay adds a subtle ricochet tail.
    const delay = ctx.createDelay(0.25);
    delay.delayTime.value = 0.075;
    const fb = ctx.createGain();
    fb.gain.value = 0.28;
    const wet = ctx.createGain();
    wet.gain.value = 0.35;

    mix.connect(out);
    mix.connect(delay);
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(out);

    lead.start(t);
    lead.stop(t + dur + 0.02);
    body.start(t);
    body.stop(t + dur + 0.02);
  }

  /**
   * Rotary-cannon brap: a burst of short noise "pops" stacked with a
   * transient square-wave click on each shot so the preset cuts through
   * the ambient bed and engine noise. Each pop has a sharp body thump
   * (low sine) for the kinetic "thud" character.
   */
  private playGatlingCannon(ctx: AudioContext, out: AudioNode, t: number): void {
    const n = 6;
    const spacing = 0.042;
    for (let i = 0; i < n; i++) {
      const start = t + i * spacing;
      this.noiseBurst(ctx, out, start, 0.05, 0.32, 1400, 6500);

      const click = ctx.createOscillator();
      click.type = 'square';
      click.frequency.setValueAtTime(1800, start);
      click.frequency.exponentialRampToValueAtTime(420, start + 0.035);
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.001, start);
      cg.gain.exponentialRampToValueAtTime(0.18, start + 0.003);
      cg.gain.exponentialRampToValueAtTime(0.001, start + 0.04);
      click.connect(cg);
      cg.connect(out);
      click.start(start);
      click.stop(start + 0.05);

      const thump = ctx.createOscillator();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(160, start);
      thump.frequency.exponentialRampToValueAtTime(70, start + 0.045);
      const tg = ctx.createGain();
      tg.gain.setValueAtTime(0.001, start);
      tg.gain.exponentialRampToValueAtTime(0.22, start + 0.004);
      tg.gain.exponentialRampToValueAtTime(0.001, start + 0.05);
      thump.connect(tg);
      tg.connect(out);
      thump.start(start);
      thump.stop(start + 0.06);
    }
  }

  private noiseBurst(
    ctx: AudioContext,
    out: AudioNode,
    t: number,
    dur: number,
    peak: number,
    lpStart: number,
    lpEnd: number
  ): void {
    if (!this.noiseBuffer) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;

    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.setValueAtTime(lpStart, t);
    filt.frequency.exponentialRampToValueAtTime(lpEnd, t + dur);
    filt.Q.value = 1.1;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + Math.max(0.002, dur * 0.1));
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);

    src.connect(filt);
    filt.connect(g);
    g.connect(out);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** Low “whump” + filtered noise + simple delay tail */
  private playPlasmaCannon(ctx: AudioContext, out: AudioNode, t: number): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(95, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.45);

    const og = ctx.createGain();
    og.gain.setValueAtTime(0.001, t);
    og.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.55);

    const mix = ctx.createGain();
    mix.gain.value = 1;

    osc.connect(og);
    og.connect(mix);

    if (this.noiseBuffer) {
      const nSrc = ctx.createBufferSource();
      nSrc.buffer = this.noiseBuffer;
      const nf = ctx.createBiquadFilter();
      nf.type = 'bandpass';
      nf.frequency.value = 350;
      nf.Q.value = 1.2;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.001, t);
      ng.gain.exponentialRampToValueAtTime(0.12, t + 0.03);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      nSrc.connect(nf);
      nf.connect(ng);
      ng.connect(mix);
      nSrc.start(t);
      nSrc.stop(t + 0.45);
    }

    const delay = ctx.createDelay(0.5);
    delay.delayTime.value = 0.084;
    const fb = ctx.createGain();
    fb.gain.value = 0.38;
    const dw = ctx.createGain();
    dw.gain.value = 0.45;

    mix.connect(dw);
    mix.connect(delay);
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(dw);
    dw.connect(out);

    osc.start(t);
    osc.stop(t + 0.6);
  }

  /** Sharp attack, bright sweep down, long decay */
  private playRailgun(ctx: AudioContext, out: AudioNode, t: number): void {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(2400, t);
    osc.frequency.exponentialRampToValueAtTime(180, t + 0.55);

    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.setValueAtTime(9000, t);
    filt.frequency.exponentialRampToValueAtTime(900, t + 0.5);

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.2, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.62);

    osc.connect(filt);
    filt.connect(g);
    g.connect(out);

    osc.start(t);
    osc.stop(t + 0.65);
  }

  /** White noise + low rumble */
  private playMissileLaunch(ctx: AudioContext, out: AudioNode, t: number): void {
    if (this.noiseBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 400;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
      src.connect(hp);
      hp.connect(g);
      g.connect(out);
      src.start(t);
      src.stop(t + 0.16);
    }

    const rumble = ctx.createOscillator();
    rumble.type = 'sine';
    rumble.frequency.setValueAtTime(62, t);
    rumble.frequency.exponentialRampToValueAtTime(38, t + 0.35);
    const rg = ctx.createGain();
    rg.gain.setValueAtTime(0.001, t);
    rg.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
    rg.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    rumble.connect(rg);
    rg.connect(out);
    rumble.start(t);
    rumble.stop(t + 0.42);
  }

  playImpact(type: 'shield' | 'hull' | 'explosion'): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;

    if (type === 'shield') {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(2200, t);
      osc.frequency.exponentialRampToValueAtTime(880, t + 0.08);

      const fm = ctx.createOscillator();
      fm.type = 'triangle';
      fm.frequency.value = 560;
      const fmg = ctx.createGain();
      fmg.gain.value = 18;
      fm.connect(fmg);
      fmg.connect(osc.frequency);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.2, t + 0.002);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      fm.start(t);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + 0.15);
      fm.stop(t + 0.15);
      return;
    }

    if (type === 'hull') {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(140, t);
      osc.frequency.exponentialRampToValueAtTime(55, t + 0.14);

      const sh = ctx.createWaveShaper();
      const curve = new Float32Array(256);
      for (let i = 0; i < 256; i++) {
        const x = (i / 128) - 1;
        curve[i] = Math.tanh(x * 3.2);
      }
      sh.curve = curve;

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.42, t + 0.006);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

      osc.connect(sh);
      sh.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + 0.25);
      return;
    }

    // explosion
    if (this.noiseBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(700, t);
      bp.frequency.exponentialRampToValueAtTime(120, t + 0.35);
      bp.Q.value = 0.6;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.45, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      src.connect(bp);
      bp.connect(g);
      g.connect(out);
      src.start(t);
      src.stop(t + 0.48);
    }

    const rumble = ctx.createOscillator();
    rumble.type = 'sine';
    rumble.frequency.setValueAtTime(48, t);
    rumble.frequency.exponentialRampToValueAtTime(28, t + 0.5);
    const rg = ctx.createGain();
    rg.gain.setValueAtTime(0.001, t);
    rg.gain.exponentialRampToValueAtTime(0.35, t + 0.04);
    rg.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    rumble.connect(rg);
    rg.connect(out);
    rumble.start(t);
    rumble.stop(t + 0.58);
  }

  playUI(type: 'select' | 'confirm' | 'error' | 'notification'): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;

    if (type === 'select') {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 1760;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.07, t + 0.003);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.045);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + 0.06);
      return;
    }

    if (type === 'confirm') {
      this.toneBeep(ctx, out, t, 660, 0.045, 0.06);
      this.toneBeep(ctx, out, t + 0.055, 990, 0.045, 0.07);
      return;
    }

    if (type === 'error') {
      this.toneBeep(ctx, out, t, 420, 0.05, 0.06);
      this.toneBeep(ctx, out, t + 0.07, 280, 0.055, 0.065);
      return;
    }

    // notification — soft fifths chime
    const freqs = [523.25, 659.25, 783.99];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      const g = ctx.createGain();
      const start = t + i * 0.055;
      g.gain.setValueAtTime(0.001, start);
      g.gain.exponentialRampToValueAtTime(0.055, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.4);
    });
  }

  private toneBeep(
    ctx: AudioContext,
    out: AudioNode,
    t: number,
    freq: number,
    attack: number,
    release: number
  ): void {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + attack);
    g.gain.exponentialRampToValueAtTime(0.001, t + attack + release);
    osc.connect(g);
    g.connect(out);
    osc.start(t);
    osc.stop(t + attack + release + 0.02);
  }

  /**
   * "Ka-ching" cash-register cue used to confirm a station-shop purchase.
   * Stacks a bright bell dyad with a quick metallic tinkle and a low
   * thud — enough acoustic mass that it cuts through engine/ambient even
   * at low master volume, but short enough (~0.45 s) to not annoy on
   * repeated buys. Distinct from {@link playPickup} so resource pickups
   * and store transactions don't sound identical.
   */
  playPurchase(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;

    // Low "credits leaving" thud — short triangle blip in the bass.
    {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(220, t);
      osc.frequency.exponentialRampToValueAtTime(110, t + 0.18);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.08, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.connect(g);
      g.connect(out);
      osc.start(t);
      osc.stop(t + 0.22);
    }

    // Bright bell dyad — perfect-fifth, sine + slight FM detune feel.
    const bell: [number, number][] = [
      [880, 0.0], [1318.51, 0.04],
    ];
    bell.forEach(([f, delay]) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      const filt = ctx.createBiquadFilter();
      filt.type = 'highpass';
      filt.frequency.value = 600;
      const g = ctx.createGain();
      const start = t + delay;
      g.gain.setValueAtTime(0.001, start);
      g.gain.exponentialRampToValueAtTime(0.085, start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.42);
      osc.connect(filt);
      filt.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.45);
    });

    // Metallic "tinkle" — three tiny square chips above the bell.
    const tinkle = [2349.32, 2637.02, 3135.96];
    tinkle.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = f;
      const g = ctx.createGain();
      const start = t + 0.06 + i * 0.04;
      g.gain.setValueAtTime(0.001, start);
      g.gain.exponentialRampToValueAtTime(0.03, start + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.08);
      osc.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.1);
    });
  }

  /** Ascending sparkly arpeggio (minor pentatonic-ish) */
  playPickup(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;
    const steps = [0, 2, 4, 7, 9, 12, 14, 16];
    const base = 523.25;
    steps.forEach((semi, i) => {
      const f = base * Math.pow(2, semi / 12);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      const filt = ctx.createBiquadFilter();
      filt.type = 'highpass';
      filt.frequency.value = 400;
      const g = ctx.createGain();
      const start = t + i * 0.038;
      g.gain.setValueAtTime(0.001, start);
      g.gain.exponentialRampToValueAtTime(0.065, start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.1);
      osc.connect(filt);
      filt.connect(g);
      g.connect(out);
      osc.start(start);
      osc.stop(start + 0.12);
    });
  }

  /** Triumphant major chord stack rising */
  playLevelUp(): void {
    this.playLevelUpInternal(1.0);
  }

  /**
   * Quieter, shorter variant played when another pilot in the sector
   * levels up. Same harmonic content so players recognize the cue, but
   * scaled to roughly a quarter of the self-gain so it reads as ambient
   * celebration rather than a solo fanfare.
   */
  playPeerLevelUp(): void {
    this.playLevelUpInternal(0.25);
  }

  private playLevelUpInternal(gainScale: number): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;

    const chords: [number, number, number][] = [
      [261.63, 329.63, 392.0],
      [293.66, 369.99, 440.0],
      [329.63, 415.3, 493.88],
      [349.23, 440.0, 523.25],
    ];

    const peak = 0.05 * gainScale;
    chords.forEach((triad, ci) => {
      triad.forEach((f) => {
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.value = f;
        const filt = ctx.createBiquadFilter();
        filt.type = 'lowpass';
        filt.frequency.value = 2200;
        filt.Q.value = 1;
        const g = ctx.createGain();
        const start = t + ci * 0.14;
        g.gain.setValueAtTime(0.001, start);
        g.gain.exponentialRampToValueAtTime(peak, start + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, start + 0.55);
        osc.connect(filt);
        filt.connect(g);
        g.connect(out);
        osc.start(start);
        osc.stop(start + 0.58);
      });
    });
  }

  /**
   * Sweep low → high with accelerating pitch curve (warp drive).
   */
  playWarp(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) return;
    const t = now(ctx);
    const out = this.masterGain;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(55, t);
    const curveDur = 1.85;
    const steps = 32;
    osc.frequency.cancelScheduledValues(t);
    osc.frequency.setValueAtTime(55, t);
    for (let i = 1; i <= steps; i++) {
      const p = i / steps;
      const eased = p * p;
      const freq = 55 * Math.pow(55 / 2400, -eased);
      osc.frequency.exponentialRampToValueAtTime(Math.min(freq, 4800), t + curveDur * p);
    }

    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.setValueAtTime(400, t);
    filt.frequency.exponentialRampToValueAtTime(12000, t + curveDur);

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.001, t + curveDur + 0.05);

    osc.connect(filt);
    filt.connect(g);
    g.connect(out);
    osc.start(t);
    osc.stop(t + curveDur + 0.1);

    if (this.noiseBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.setValueAtTime(200, t);
      hp.frequency.exponentialRampToValueAtTime(6000, t + curveDur);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.001, t);
      ng.gain.exponentialRampToValueAtTime(0.04, t + 0.2);
      ng.gain.exponentialRampToValueAtTime(0.001, t + curveDur);
      src.connect(hp);
      hp.connect(ng);
      ng.connect(out);
      src.start(t);
      src.stop(t + curveDur + 0.05);
    }
  }

  /**
   * @param speed 0 = idle (silent hum), 1 = full thrust
   */
  /**
   * Smoothly crossfades the thruster between idle and full burn.
   * - Bandpass sweeps 260 Hz → 1800 Hz (air rush getting brighter)
   * - Highpass opens from 180 Hz → 320 Hz at high throttle (less rumble mud)
   * - Noise gain grows non-linearly so idle stays subtle
   * - Sub rumble fades in above ~50% throttle
   * Uses setTargetAtTime exclusively so real audio parameters transition
   * with an exponential slew — no steps, no pops.
   * @param speed 0 = idle (near-silent), 1 = full thrust
   */
  setEngineSpeed(speed: number): void {
    this.engineSpeed = clamp01(speed);
    const ctx = this.ensureCtx();
    if (!ctx || !this.engineNoiseGain || !this.engineBandpass || !this.engineHighpass) return;
    const t = now(ctx);
    const s = this.engineSpeed;

    const bpMin = 260, bpMax = 1800;
    const bpFreq = bpMin + (bpMax - bpMin) * Math.pow(s, 0.8);
    this.engineBandpass.frequency.cancelScheduledValues(t);
    this.engineBandpass.frequency.setTargetAtTime(bpFreq, t, 0.25);

    const hpFreq = 180 + 140 * s;
    this.engineHighpass.frequency.cancelScheduledValues(t);
    this.engineHighpass.frequency.setTargetAtTime(hpFreq, t, 0.3);

    // Main noise body: silent at s=0, audible but subtle at idle movement,
    // strong at full thrust. Quadratic curve keeps idle clean.
    const noiseG = 0.22 * Math.pow(s, 1.4);
    this.engineNoiseGain.gain.cancelScheduledValues(t);
    this.engineNoiseGain.gain.setTargetAtTime(noiseG, t, 0.18);

    if (this.engineRumbleGain && this.engineRumble) {
      // Rumble only kicks in past ~30% throttle.
      const rActivation = Math.max(0, (s - 0.3) / 0.7);
      const rumbleG = 0.09 * Math.pow(rActivation, 1.2);
      this.engineRumbleGain.gain.cancelScheduledValues(t);
      this.engineRumbleGain.gain.setTargetAtTime(rumbleG, t, 0.22);

      // Very small pitch bend on the rumble for a bit of "spooling".
      const rumbleF = 44 + 14 * s;
      this.engineRumble.frequency.cancelScheduledValues(t);
      this.engineRumble.frequency.setTargetAtTime(rumbleF, t, 0.25);
    }
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = clamp01(volume);
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(this.masterVolume, this.ctx?.currentTime ?? 0);
    }
  }

  startAmbient(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain || this.ambientRunning) return;

    const t = now(ctx);
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.062;

    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.07;

    const ambMaster = ctx.createGain();
    ambMaster.gain.value = 0.14;

    lfo.connect(lfoGain);

    const freqs = [38, 52, 73, 105];
    const detunes = [-6, 4, -3, 7];
    const oscs: OscillatorNode[] = [];

    freqs.forEach((hz, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = hz;
      o.detune.value = detunes[i] * 8;
      const g = ctx.createGain();
      g.gain.value = 0.22 + i * 0.06;
      lfoGain.connect(g.gain);
      o.connect(g);
      g.connect(ambMaster);
      o.start(t);
      oscs.push(o);
    });

    ambMaster.connect(this.masterGain);
    lfo.start(t);

    this.ambientOscs = oscs;
    this.ambientLfo = lfo;
    this.ambientLfoGain = lfoGain;
    this.ambientMaster = ambMaster;
    this.ambientRunning = true;
  }

  /**
   * Convenience entry-point for the pre-game "main screen" intro bed.
   *
   * Must be called from a user-gesture handler so the underlying
   * AudioContext + HTMLAudioElement are allowed to start. Subsequent
   * calls are cheap — `startMusic` no-ops when the URL is unchanged and
   * just adjusts the gain. Calling this after switching to the in-game
   * track (e.g. on sign-out / exit-to-menu) cleanly swaps back to the
   * intro bed.
   */
  playIntroMusic(): void {
    this.init();
    // Vite rewrites `BASE_URL` to `./` for the Electron / Steam desktop
    // build (loaded over `file://`) and to `/` for the web + Capacitor
    // builds — see `vite.config.ts`. Using it as a prefix keeps the
    // same code working in all three deployment targets.
    this.startMusic(`${import.meta.env.BASE_URL}music/intro-astro-alloy.mp3`, 0.6);
  }

  /**
   * Start (or switch) a looping background-music track.
   *
   * Safe to call multiple times — subsequent calls either swap the source
   * (if URL changed) or just update the volume. `volume` is a music-only
   * multiplier applied in addition to the master gain (0..1).
   */
  startMusic(url: string, volume = 0.1): void {
    const clampedVol = clamp01(volume);
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain) {
      this.pendingMusic = { url, volume: clampedVol };
      return;
    }

    if (this.musicElement && this.musicElement.src.endsWith(url)) {
      if (this.musicGain) {
        this.musicGain.gain.setTargetAtTime(clampedVol, now(ctx), 0.1);
      }
      if (this.musicElement.paused && this.musicEnabled) {
        void this.musicElement.play().catch(() => {
          /* blocked by autoplay policy — user gesture will retry */
        });
      }
      return;
    }

    this.stopMusic();

    const el = new Audio(url);
    el.loop = true;
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';

    const src = ctx.createMediaElementSource(el);
    const g = ctx.createGain();
    g.gain.value = clampedVol;

    src.connect(g);
    g.connect(this.masterGain);

    this.musicElement = el;
    this.musicSource = src;
    this.musicGain = g;

    if (this.musicEnabled) {
      void el.play().catch(() => {
        /* blocked by autoplay policy — `init` is gesture-triggered so this
           is usually fine, but silently ignore if the browser disagrees. */
      });
    } else {
      el.pause();
    }
  }

  /**
   * Toggle background music playback without tearing down the media
   * element, so re-enabling resumes where we left off.
   */
  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    if (!this.musicElement) return;
    if (enabled) {
      void this.musicElement.play().catch(() => {
        /* autoplay policy — will retry on next user gesture */
      });
    } else {
      this.musicElement.pause();
    }
  }

  isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  stopMusic(): void {
    if (this.musicElement) {
      try {
        this.musicElement.pause();
        this.musicElement.removeAttribute('src');
        this.musicElement.load();
      } catch {
        /* */
      }
      this.musicElement = null;
    }
    if (this.musicSource) {
      try {
        this.musicSource.disconnect();
      } catch {
        /* */
      }
      this.musicSource = null;
    }
    if (this.musicGain) {
      try {
        this.musicGain.disconnect();
      } catch {
        /* */
      }
      this.musicGain = null;
    }
  }

  /** Adjust the music-only gain without re-loading the track. */
  setMusicVolume(volume: number): void {
    const clamped = clamp01(volume);
    if (this.pendingMusic) {
      this.pendingMusic.volume = clamped;
      return;
    }
    if (!this.musicGain || !this.ctx) return;
    this.musicGain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.1);
  }

  stopAmbient(): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.ambientRunning) return;
    const t = now(ctx);

    if (this.ambientMaster && this.masterGain) {
      try {
        this.ambientMaster.disconnect(this.masterGain);
      } catch {
        /* */
      }
    }

    this.ambientOscs.forEach((o) => {
      try {
        o.stop(t);
      } catch {
        /* already stopped */
      }
    });
    this.ambientOscs = [];

    if (this.ambientLfo) {
      try {
        this.ambientLfo.stop(t);
      } catch {
        /* */
      }
      this.ambientLfo = null;
    }

    this.ambientLfoGain = null;
    this.ambientMaster = null;
    this.ambientRunning = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.stopAmbient();
    this.stopMusic();

    const ctx = this.ctx;
    if (ctx) {
      const t = now(ctx);
      if (this.engineNoise) {
        try {
          this.engineNoise.stop(t);
        } catch {
          /* */
        }
      }
      if (this.engineRumble) {
        try {
          this.engineRumble.stop(t);
        } catch {
          /* */
        }
      }
      void ctx.close();
    }

    this.ctx = null;
    this.masterGain = null;
    this.noiseBuffer = null;
    this.engineNoise = null;
    this.engineNoiseGain = null;
    this.engineBandpass = null;
    this.engineHighpass = null;
    this.engineRumble = null;
    this.engineRumbleGain = null;
  }
}
